import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface Session {
  id: string;
  owner_id: string;
  appointment_id: string | null;
  contract_id: string | null;
  client_id: string | null;
  client_name: string;
  project_name: string;
  status: string;
  photos: string[];
  notes: string;
  started_at: string;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
  // New fields
  body_zone: string | null;
  style: string | null;
  price: number | null;
  elapsed_seconds: number;
  timer_started_at: string | null;
  team_member_id: string | null;
}

export interface SessionMaterial {
  id: string;
  session_id: string;
  product_id: string;
  quantity: number;
  lot_number: string | null;
  expiration_date: string | null;
  opening_date: string | null;
  notes: string | null;
  created_at: string;
}

// ─── Body zones cascade data ──────────────────────────────────────────────────

export const BODY_ZONES: Record<string, string[]> = {
  'Tête & Cou': ['Crâne', 'Nuque', 'Cou', 'Visage', 'Front', 'Tempe gauche', 'Tempe droite', 'Oreille gauche', 'Oreille droite', 'Mâchoire', 'Menton'],
  'Bras': ['Épaule gauche', 'Épaule droite', 'Biceps gauche', 'Biceps droit', 'Triceps gauche', 'Triceps droit', 'Coude gauche', 'Coude droit', 'Avant-bras gauche', 'Avant-bras droit', 'Poignet gauche', 'Poignet droit', 'Main gauche', 'Main droite', 'Doigts gauche', 'Doigts droit'],
  'Torse': ['Poitrine gauche', 'Poitrine droite', 'Sternum', 'Côtes gauches', 'Côtes droites', 'Ventre', 'Nombril', 'Hanche gauche', 'Hanche droite', 'Aine gauche', 'Aine droite'],
  'Dos': ['Haut du dos', 'Bas du dos', 'Colonne vertébrale', 'Omoplate gauche', 'Omoplate droite', 'Lombaires', 'Fesses gauche', 'Fesses droite'],
  'Jambes': ['Cuisse gauche externe', 'Cuisse gauche interne', 'Cuisse droite externe', 'Cuisse droite interne', 'Genou gauche', 'Genou droit', 'Tibia gauche', 'Tibia droit', 'Mollet gauche', 'Mollet droit', 'Cheville gauche', 'Cheville droite', 'Pied gauche', 'Pied droit', 'Orteils gauche', 'Orteils droit'],
  'Autres': ['Autre zone'],
};

export const TATTOO_STYLES = [
  'Réaliste', 'Néo-traditionnel', 'Japonais', 'Tribal', 'Géométrique',
  'Aquarelle', 'Old School / Américain', 'Fine Line', 'Lettering',
  'Blackwork', 'Pointillisme', 'Biomécanique', 'Horreur', 'Minimaliste', 'Autre',
];

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useSessions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['sessions', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('owner_id', user!.id)
        .order('started_at', { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as Session[];
    },
    enabled: !!user,
  });
}

export function useSession(id: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['session', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', id!)
        .eq('owner_id', user!.id)
        .single();
      if (error) throw error;
      return data as unknown as Session;
    },
    enabled: !!id && !!user,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: 500,
  });
}

export function useActiveSession() {
  const { data: sessions = [] } = useSessions();
  return sessions.find(s => s.status === 'in_progress') || null;
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useStartSession() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      client_name: string; client_id?: string | null;
      project_name?: string; appointment_id?: string; contract_id?: string;
      body_zone?: string; style?: string; price?: number;
    }) => {
      const { data: result, error } = await supabase.from('sessions').insert({
        owner_id: user!.id,
        client_name: data.client_name,
        client_id: data.client_id || null,
        project_name: data.project_name || '',
        appointment_id: data.appointment_id || null,
        contract_id: data.contract_id || null,
        body_zone: data.body_zone || null,
        style: data.style || null,
        price: data.price || null,
        status: 'in_progress',
        elapsed_seconds: 0,
      }).select().single();
      if (error) throw error;
      return result as unknown as Session;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

export function useEndSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, elapsed_seconds }: { id: string; elapsed_seconds: number }) => {
      const { error } = await supabase.from('sessions').update({
        status: 'completed',
        ended_at: new Date().toISOString(),
        elapsed_seconds,
        timer_started_at: null,
      }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
}

export function useUpdateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Session> & { id: string }) => {
      const { error } = await supabase.from('sessions').update(updates as any).eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
      qc.invalidateQueries({ queryKey: ['session', vars.id] });
    },
  });
}

// Session Materials
export function useSessionMaterials(sessionId: string | null) {
  return useQuery({
    queryKey: ['session_materials', sessionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('session_materials')
        .select('*')
        .eq('session_id', sessionId!);
      if (error) throw error;
      return (data || []) as unknown as SessionMaterial[];
    },
    enabled: !!sessionId,
  });
}

export function useAddSessionMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ session_id, product_id, quantity, lot_number, expiration_date, opening_date, notes }: {
      session_id: string; product_id: string; quantity: number;
      lot_number?: string; expiration_date?: string; opening_date?: string; notes?: string;
    }) => {
      const { error } = await supabase.from('session_materials').insert({
        session_id, product_id, quantity,
        lot_number: lot_number || null,
        expiration_date: expiration_date || null,
        opening_date: opening_date || null,
        notes: notes || null,
      });
      if (error) throw error;
      const { data: product } = await supabase.from('products').select('stock').eq('id', product_id).single();
      if (product) {
        await supabase.from('products').update({ stock: Math.max(0, (product.stock as number) - quantity) }).eq('id', product_id);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session_materials'] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useRemoveSessionMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('session_materials').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session_materials'] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}


